import { useLingui } from '@lingui/react/macro';

import { ImageType } from '../@types/global';

/**
 * Converts a string such as patient_id to patientId
 * Written based upon https://coderwall.com/p/iprsng/convert-snake-case-to-camelcase
 *
 * @param {*} string
 */
export const convertSnakeToCamelCase = (string: any) => {
  const find = /(_\w)/g;
  const convert = (matches: any) => matches[1].toUpperCase();

  return string.replace(find, convert);
};

/**
 * Capitalize the first letter of a string
 *
 * @param {*} string
 */
export const capitalize = (string: any) =>
  string.charAt(0).toUpperCase() + string.slice(1);

/**
 * Convert a string to proper business name formatting
 * Handles apostrophes, hyphens, and business suffixes properly for company names
 * Converts from ALL CAPS to proper case while preserving business suffixes and special company names
 *
 * @param {*} string
 */
export const toBusinessName = (string: any) => {
  if (!string) return string;

  // First, protect business suffixes, special company names, state abbreviations, and common words by temporarily replacing them
  const businessSuffixes = [
    'LLC',
    'CORP',
    'LTD',
    'LP',
    'LLP',
    'PC',
    'MetLife',
    // US State abbreviations
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ];

  // Common words that should remain lowercase (except at the beginning)
  const lowercaseWords = [
    'and',
    'of',
    'the',
    'for',
    'in',
    'on',
    'at',
    'to',
    'by',
    'with',
  ];

  let result = string.toLowerCase();

  // Replace business suffixes with placeholders
  const placeholders: { [key: string]: string } = {};
  businessSuffixes.forEach((suffix, index) => {
    const placeholder = `__BUSINESS_SUFFIX_${index}__`;
    placeholders[placeholder] = suffix;
    result = result.replace(
      new RegExp(`\\b${suffix.toLowerCase()}\\b`, 'g'),
      placeholder,
    );
  });

  // Replace lowercase words with placeholders (except at the beginning of the string)
  lowercaseWords.forEach((word, index) => {
    const placeholder = `__LOWERCASE_WORD_${index}__`;
    placeholders[placeholder] = word;
    // Only replace if not at the beginning of the string
    result = result.replace(
      new RegExp(`(?<!^)\\b${word}\\b`, 'g'),
      placeholder,
    );
  });

  // Apply title case logic
  result = result.replace(/(?:^|\s|[-'])\w/g, (match: string) => {
    // Don't capitalize if it's a single letter after apostrophe (like possessive 's')
    if (match.length === 2 && match[0] === "'") {
      return match; // Keep lowercase
    }
    return match.toUpperCase();
  });

  // Restore business suffixes
  Object.entries(placeholders).forEach(([placeholder, suffix]) => {
    result = result.replace(placeholder, suffix);
  });

  return result;
};

/**
 * maps long image type to short label
 *
 */
export const useMapImageTypeToLabel = () => {
  const { t } = useLingui();
  return {
    [ImageType.BITEWING]: t`BW`,
    [ImageType.PERIAPICAL]: t`PA`,
    [ImageType.PANORAMIC]: t`Pano`,
    [ImageType.COLOR_IMAGE]: t`COLOR`,
    [ImageType.INTRAORAL]: t`IO`,
    [ImageType.CONE_BEAM_CT]: t`CBCT`,
    [ImageType.OTHER]: t`Other`,
  };
};
